import type { DefaultTheme } from 'styled-components';
import { darkColors } from '#/config/themes/dark';
import { lightColors } from '#/config/themes/light';

/**
 * Checks if the dark theme is currently applied by comparing the theme object
 * with the dark theme configuration.
 *
 * @param theme - The styled-components theme object
 * @returns true if dark theme is active, false otherwise
 *
 * @example
 * // In a styled component:
 * const MyComponent = styled.div`
 *   ${({ theme }) => isDarkTheme(theme) && `
 *     background: #000;
 *     color: #fff;
 *   `}
 * `;
 */
export const isDarkTheme = (theme: DefaultTheme): boolean => {
  // Check multiple distinctive color values to reliably detect dark theme
  const customColors = (theme.colors as any).custom;

  if (!customColors) {
    return false;
  }

  // Compare multiple colors to ensure accurate detection
  return (
    customColors.formBackground === darkColors.custom?.formBackground &&
    customColors.cardBackground === darkColors.custom?.cardBackground &&
    customColors.contrastText === darkColors.custom?.contrastText
  );
};

/**
 * Checks if the light theme is currently applied by comparing the theme object
 * with the light theme configuration.
 *
 * @param theme - The styled-components theme object
 * @returns true if light theme is active, false otherwise
 *
 * @example
 * // In a styled component:
 * const MyComponent = styled.div`
 *   ${({ theme }) => isLightTheme(theme) && `
 *     background: #fff;
 *     color: #000;
 *   `}
 * `;
 */
export const isLightTheme = (theme: DefaultTheme): boolean => {
  // Check multiple distinctive color values to reliably detect light theme
  const customColors = (theme.colors as any).custom;

  if (!customColors) {
    return false;
  }

  // Compare multiple colors to ensure accurate detection
  return (
    customColors.formBackground === lightColors.custom?.formBackground &&
    customColors.cardBackground === lightColors.custom?.cardBackground &&
    customColors.contrastText === lightColors.custom?.contrastText
  );
};

/**
 * Returns the current theme name
 *
 * @param theme - The styled-components theme object
 * @returns 'dark', 'light', or 'unknown'
 *
 * @example
 * const MyComponent = styled.div`
 *   ${({ theme }) => {
 *     const themeName = getThemeName(theme);
 *     switch (themeName) {
 *       case 'dark':
 *         return 'filter: invert(1);';
 *       case 'light':
 *         return 'filter: none;';
 *       default:
 *         return '';
 *     }
 *   }}
 * `;
 */
export const getThemeName = (theme: DefaultTheme): 'dark' | 'light' | 'unknown' => {
  if (isDarkTheme(theme)) {
    return 'dark';
  }
  if (isLightTheme(theme)) {
    return 'light';
  }
  return 'unknown';
};
